import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom"; // Adicionado useNavigate
import axios from "axios";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { fetchToken } from "./Auth"; // <--- 1. Importação Essencial
import "../css/Gantt3.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// --- Funções Auxiliares ---
const parseDate = (dateStr) => {
  if (!dateStr || dateStr === "0000-00-00") return null;
  const date = new Date(dateStr);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const adjustedDate = new Date(date.getTime() + userTimezoneOffset);

  return isNaN(adjustedDate.getTime()) || adjustedDate.getFullYear() < 1900
    ? null
    : adjustedDate.getTime();
};

const formatDateBr = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

function Gantt3() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projeto, setProjeto] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Busca os dados do projeto (COM TOKEN)
  useEffect(() => {
    const token = fetchToken();

    if (!token) {
      alert("Sessão expirada.");
      navigate("/login");
      return;
    }

    setLoading(true);
    axios
      .get(`/api/projetocompleto/${id}`, {
        headers: { Authorization: `Bearer ${token}` }, // <--- 2. Correção Aqui
      })
      .then((res) => {
        if (res.data.Status === "Success" && res.data.Result.length > 0) {
          setProjeto(res.data.Result[0]);
        } else {
          alert("Projeto não encontrado ou acesso negado.");
        }
      })
      .catch((err) => {
        console.error("Erro ao buscar projeto:", err);
        if (
          err.response &&
          (err.response.status === 401 || err.response.status === 403)
        ) {
          alert("Sem permissão.");
          navigate("/projetocompleto");
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // 2. Prepara a configuração do gráfico
  const chartConfig = useMemo(() => {
    if (!projeto) return null;

    const etapasRaw = [
      {
        label: "1. Estruturação",
        start: projeto.data_inicio_etapa1,
        end: projeto.data_termino_etapa1,
      },
      {
        label: "2. TdR e Orçamento",
        start: projeto.data_inicio_etapa2,
        end: projeto.data_termino_etapa2,
      },
      {
        label: "3. Licitação",
        start: projeto.data_inicio_etapa3,
        end: projeto.data_termino_etapa3,
      },
      {
        label: "4. Contrato",
        start: projeto.data_inicio_etapa4,
        end: projeto.data_termino_etapa4,
      },
      {
        label: "5. Execução",
        start: projeto.data_ois,
        end: projeto.data_termino_etapa5,
      },
      {
        label: "6. Encerramento",
        start: projeto.data_inicio_etapa6,
        end: projeto.data_termino_etapa6,
      },
    ];

    const etapasValidas = etapasRaw
      .map((e) => ({
        ...e,
        startMs: parseDate(e.start),
        endMs: parseDate(e.end),
      }))
      .filter((e) => e.startMs && e.endMs && e.endMs >= e.startMs);

    if (etapasValidas.length === 0) return null;

    const allDates = etapasValidas.flatMap((e) => [e.startMs, e.endMs]);
    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const margin = 432000000; // 5 dias

    return {
      data: {
        labels: etapasValidas.map((e) => e.label),
        datasets: [
          {
            label: "Período",
            data: etapasValidas.map((e) => [e.startMs, e.endMs]),
            backgroundColor: [
              "#3B82F6",
              "#10B981",
              "#8B5CF6",
              "#F59E0B",
              "#EF4444",
              "#6366F1",
            ],
            borderWidth: 0,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { left: 0, right: 20, top: 0, bottom: 0 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1e293b",
            padding: 12,
            titleFont: { size: 14 },
            bodyFont: { size: 13 },
            callbacks: {
              title: (ctx) => ctx[0].label,
              label: (ctx) => {
                const start = ctx.raw[0];
                const end = ctx.raw[1];
                const dias =
                  Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                return `${formatDateBr(start)} até ${formatDateBr(end)} (${dias} dias)`;
              },
            },
          },
        },
        scales: {
          x: {
            position: "top",
            min: minDate - margin,
            max: maxDate + margin,
            type: "linear",
            grid: {
              color: "#e2e8f0",
              borderDash: [5, 5],
              drawBorder: false,
            },
            ticks: {
              color: "#64748b",
              font: { size: 11 },
              callback: (value) => formatDateBr(value),
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            grid: { display: false, drawBorder: false },
            ticks: {
              color: "#334155",
              font: { size: 12, weight: "600" },
              padding: 15,
            },
          },
        },
      },
    };
  }, [projeto]);

  if (loading) {
    return <div className="gantt-state">Carregando dados...</div>;
  }

  if (!projeto) {
    return (
      <div className="gantt-state">Projeto não encontrado ou sem acesso.</div>
    );
  }

  return (
    <main className="gantt-page">
      <div className="gantt-container">
        <div className="top-action-bar">
          <Link to="/projetocompleto" className="btn-voltar-big">
            Voltar para Projetos
          </Link>
        </div>

        <header className="gantt-header-center">
          <h1>{projeto.nomeprojeto}</h1>
          <span className="subtitle">Cronograma Físico de Execução</span>
        </header>

        <div className="chart-wrapper">
          {chartConfig ? (
            <Bar data={chartConfig.data} options={chartConfig.options} />
          ) : (
            <div className="empty-msg">
              Este projeto ainda não possui datas válidas cadastradas para gerar
              o gráfico.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default Gantt3;
